import {
  Stack, Text, Group, Button, Box, Code, Alert, PinInput, Loader,
  CopyButton, ActionIcon, Tooltip, PasswordInput,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import {
  IconCopy, IconCheck, IconAlertTriangle, IconLock,
} from '@tabler/icons-react'
import { useMutation } from '@tanstack/react-query'
import { QRCodeSVG } from 'qrcode.react'
import { useState } from 'react'
import { mfaApi, type TotpBeginResponse } from '@/api/mfa'
import { formatApiError } from '@/api/errors'
import { useAuthStore } from '@/store/auth'

interface Props {
  onComplete: () => void
}

export function SetupWizardStepTotp({ onComplete }: Props) {
  const { logout, refreshUser } = useAuthStore()
  const [setupData, setSetupData] = useState<TotpBeginResponse | null>(null)
  const [code, setCode] = useState('')
  const [pwInput, setPwInput] = useState('')
  const [pwError, setPwError] = useState('')

  const beginMutation = useMutation({
    mutationFn: (password: string) => mfaApi.totpBegin(password),
    onSuccess: (data) => {
      setSetupData(data)
      setCode('')
    },
    onError: (err: any) => {
      const detail = err?.response?.data?.detail
      if (detail === 'invalid_password') {
        setPwError('Senha incorreta. Tente novamente.')
      } else if (detail === 'password_quality_failed') {
        setPwError('Sua senha atual não atende a política do Kanidm. Troque-a antes de configurar TOTP.')
      } else {
        notifications.show({
          color: 'red', title: 'Erro ao iniciar setup',
          message: formatApiError(err, 'Falha contatando Kanidm'),
        })
      }
    },
  })

  const verifyMutation = useMutation({
    mutationFn: ({ token, value }: { token: string; value: number }) => {
      if (!token || !Number.isInteger(value) || value < 0 || value > 999999) {
        return Promise.reject(new Error('Código inválido — digite os 6 dígitos'))
      }
      return mfaApi.totpVerify(token, value)
    },
    onSuccess: async () => {
      notifications.show({
        color: 'teal', title: 'TOTP configurado',
        message: 'Você esta protegido com 2FA.',
      })
      setSetupData(null)
      setCode('')
      await refreshUser()
      onComplete()
    },
    onError: (err) => {
      notifications.show({
        color: 'red', title: 'Código inválido',
        message: formatApiError(err, 'Tente novamente — código expira em 30s.'),
      })
      setCode('')
    },
  })

  const triggerVerify = (raw: string) => {
    if (!setupData || raw.length !== 6) return
    const value = parseInt(raw, 10)
    if (Number.isNaN(value)) return
    verifyMutation.mutate({ token: setupData.session_token, value })
  }

  // Step 1: pedir senha (sempre — politica MfaRequired exige password+TOTP juntos)
  if (!setupData) {
    return (
      <Stack gap="md">
        <Box>
          <Text fw={700} size="md" c="#e9ecef">Configure 2FA (TOTP)</Text>
          <Text size="xs" c="#6c757d" mt={2}>
            Confirme sua senha para liberar o setup do app autenticador.
          </Text>
        </Box>
        <form
          onSubmit={(e) => {
            e.preventDefault()
            if (pwInput.length === 0) { setPwError('Informe sua senha.'); return }
            setPwError('')
            beginMutation.mutate(pwInput)
          }}
        >
          <Stack gap="sm">
            <PasswordInput
              label="Sua senha atual"
              placeholder="Digite sua senha"
              value={pwInput}
              onChange={(e) => { setPwInput(e.currentTarget.value); setPwError('') }}
              leftSection={<IconLock size={16} />}
              error={pwError || undefined}
              autoFocus
              required
              data-sensitive="true"
              styles={{
                label: { color: '#b6c2c9', fontWeight: 600, fontSize: 12, marginBottom: 4 },
                input: {
                  backgroundColor: 'var(--ai-auth-input-bg)',
                  borderColor: 'var(--ai-auth-border)',
                  color: '#e9ecef',
                },
              }}
            />
            <Button
              type="submit" fullWidth size="md" radius="sm"
              loading={beginMutation.isPending}
              disabled={!pwInput}
              style={{
                backgroundColor: pwInput ? 'var(--ai-brand-verde)' : undefined,
                border: 'none',
                fontWeight: 700,
                fontSize: 13,
              }}
            >
              {beginMutation.isPending ? 'Validando...' : 'Continuar'}
            </Button>
            <Button type="button" variant="subtle" size="xs" color="gray"
              onClick={() => { void logout() }}>
              Sair
            </Button>
          </Stack>
        </form>
      </Stack>
    )
  }

  return (
    <Stack gap="md">
      <Box>
        <Text fw={700} size="md" c="#e9ecef">Configure 2FA (TOTP)</Text>
        <Text size="xs" c="#6c757d" mt={2}>
          Use Google Authenticator, Aegis, 1Password ou Bitwarden.
        </Text>
      </Box>

      <Box p="md" bg="white" style={{ borderRadius: 8, alignSelf: 'center' }}>
        <QRCodeSVG value={setupData.otpauth_url} size={180} level="M" />
      </Box>

      <Stack gap={2}>
        <Text size="xs" c="#6c757d">Conta: <Code>{setupData.accountname}</Code></Text>
        <Text size="xs" c="#6c757d">
          Algoritmo: <Code>{setupData.algo}</Code> · {setupData.digits} dígitos · {setupData.step}s
        </Text>
      </Stack>

      <Alert icon={<IconAlertTriangle size={16} />} color="yellow" variant="light">
        <Stack gap={4}>
          <Text size="xs" fw={600}>Caso scan não funcione, digite o segredo manualmente:</Text>
          <Group gap="xs">
            <Code style={{ fontSize: 11, wordBreak: 'break-all' }}>{setupData.secret_b32}</Code>
            <CopyButton value={setupData.secret_b32}>
              {({ copied, copy }) => (
                <Tooltip label={copied ? 'Copiado' : 'Copiar'}>
                  <ActionIcon variant="subtle" onClick={copy}>
                    {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                  </ActionIcon>
                </Tooltip>
              )}
            </CopyButton>
          </Group>
        </Stack>
      </Alert>

      <form
        onSubmit={(e) => {
          e.preventDefault()
          triggerVerify(code)
        }}
      >
        <Stack gap="xs" align="center">
          <Text size="sm" c="#b6c2c9">Digite o código de 6 dígitos:</Text>
          <PinInput
            length={6} type="number"
            value={code}
            onChange={setCode}
            onComplete={triggerVerify}
            autoFocus
            disabled={verifyMutation.isPending}
            styles={{
              input: {
                backgroundColor: 'var(--ai-auth-input-bg)',
                borderColor: 'var(--ai-auth-border)',
                color: '#e9ecef',
              },
            }}
          />
          <Button
            type="submit"
            fullWidth
            disabled={code.length !== 6}
            loading={verifyMutation.isPending}
            mt={8}
            size="md"
            radius="sm"
            style={{
              backgroundColor: code.length === 6 ? 'var(--ai-brand-verde)' : undefined,
              border: 'none',
              fontWeight: 700,
              fontSize: 13,
            }}
          >
            Confirmar
          </Button>
        </Stack>
      </form>

      <Button
        type="button" variant="subtle" size="xs" color="gray"
        onClick={() => { void logout() }}
      >
        Sair
      </Button>
    </Stack>
  )
}
